import { format } from 'date-fns';

/**
 * A value that exists on both sides: what our database says it should be, and what Entra holds.
 *
 * Kept free of any rendering so it can be reasoned about - and tested - on its own. The Microsoft tab
 * builds the labelled, renderable rows on top of this shape.
 */
export interface EntraComparison {
  /** what our database says the value should be */
  local: unknown;
  /** what the snapshot says Entra actually holds */
  entra: unknown;
  /**
   * Shown, but never counted as drift.
   *
   * ! Only for values we cannot make true. ! `proxyAddresses` is the case this exists for: it decides
   * which addresses actually deliver, so it belongs on the tab, but Graph refuses to write it. Counting
   * it as a difference would paint the card red and offer a repair button that cannot possibly help -
   * which is worse than not showing it at all.
   */
  informational?: boolean;
}

/**
 * Reduces a value to the string its two sides are compared by.
 *
 * ! Both sides always go through here. ! Comparing a raw `Date` against a formatted string, or an array
 * against a joined string, is what made the old checks answer "different" for values that were the same.
 *
 * The rules, all of them:
 * - null, undefined and the empty string all mean "nothing" and compare equal
 * - dates compare by the minute, because Entra answers in whole seconds and we store milliseconds
 * - arrays compare as sets: order is not something Entra preserves
 * - comparison is case insensitive, because Entra treats names and mail addresses that way. A pure
 *   case difference is not worth painting the tab red and offering a repair that would change nothing.
 * @param value either side of a comparison
 */
export function normalizeForComparison(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(normalizeForComparison).filter(Boolean).sort().join('\n');
  if (value instanceof Date) return format(value, 'yyyy-MM-dd HH:mm');
  if (typeof value === 'boolean') return value ? 'ja' : 'nein';
  return String(value).trim().toLowerCase();
}

/** Whether our database and Entra agree on this value. @see normalizeForComparison */
export function isInSync(local: unknown, entra: unknown): boolean {
  return normalizeForComparison(local) === normalizeForComparison(entra);
}

/**
 * The values that need attention.
 *
 * ! The whole tab derives its state from this one function. ! The badge in the card header, the
 * checkmark on each row and whether the repair button appears are all this list. They used to be three
 * separate implementations of "are these equal" and they disagreed - the badge compared a field that
 * had no row, and read one that does not exist on a person at all, so a card sat there red while every
 * row inside it showed a green check and nothing told the user what was wrong.
 * @param rows every compared value
 */
export const outOfSyncRows = <T extends EntraComparison>(rows: T[]): T[] =>
  rows.filter(row => !row.informational && !isInSync(row.local, row.entra));

/**
 * Renders a value the way a person reads it, which is not the way it is compared - "Kein Eintrag" is
 * friendlier than an empty cell, but it must never be what decides whether something is in sync.
 * @param value the value to display
 */
export function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Kein Eintrag';
  if (Array.isArray(value)) return value.length ? value.map(formatValue).join(', ') : 'Kein Eintrag';
  if (value instanceof Date) return format(value, 'dd.MM.yyyy HH:mm');
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
  return String(value);
}
